<!-- src/components/mailer/MailerStudio.vue -->
<template>
  <section class="core-mailer">
    <div class="core-mailer-header">
      <div>
        <h4>Guest Mailer</h4>

        <p class="grey-text text-darken-1">
          {{ venueName || venueId }}
        </p>
      </div>

      <div class="core-mailer-header-actions">
        <span class="chip"> {{ subscribedGuests.length }} subscribed </span>

        <button type="button" class="btn-flat" :disabled="busy" @click="refreshAll">
          Refresh
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="card-panel red lighten-5 red-text text-darken-3">
      {{ errorMessage }}
    </div>

    <div
      v-if="successMessage"
      class="card-panel green lighten-5 green-text text-darken-3"
    >
      {{ successMessage }}
    </div>

    <div class="core-mailer-tabs" role="tablist" aria-label="Mailer sections">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="btn-flat core-mailer-tab"
        :class="{
          'core-mailer-tab--active': activeTab === tab.key,
        }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="initialLoading" class="card">
      <div class="card-content">
        <p>Loading mailer data…</p>
      </div>
    </div>

    <template v-else>
      <!-- GUESTS -->
      <section v-if="activeTab === 'guests'" class="core-mailer-panel">
        <div class="core-mailer-toolbar">
          <div class="input-field core-mailer-search">
            <input
              id="mailer-guest-search"
              v-model="guestSearch"
              type="search"
              autocomplete="off"
            />

            <label
              for="mailer-guest-search"
              :class="{
                active: guestSearch,
              }"
            >
              Search guests
            </label>
          </div>

          <div class="core-mailer-toolbar-actions">
            <button
              v-if="selectedGuestCount > 0"
              type="button"
              class="btn red darken-2"
              :disabled="busy"
              @click="removeSelectedGuests"
            >
              Delete selected ({{ selectedGuestCount }})
            </button>

            <button type="button" class="btn-flat" @click="downloadGuestCsvExample">
              CSV example
            </button>

            <button
              type="button"
              class="btn blue-grey darken-2"
              :disabled="busy"
              @click="csvInput?.click()"
            >
              Import CSV
            </button>

            <input
              ref="csvInput"
              class="core-mailer-hidden-input"
              type="file"
              accept=".csv,text/csv"
              @change="handleCsvFile"
            />
          </div>
        </div>

        <div class="card core-mailer-add-guest">
          <div class="card-content">
            <span class="card-title"> Add guest </span>

            <div class="row">
              <div class="input-field col s12 m2">
                <input
                  id="guest-first-name"
                  v-model="manualGuest.first_name"
                  type="text"
                  autocomplete="off"
                />

                <label
                  for="guest-first-name"
                  :class="{
                    active: manualGuest.first_name,
                  }"
                >
                  First name
                </label>
              </div>

              <div class="input-field col s12 m2">
                <input
                  id="guest-last-name"
                  v-model="manualGuest.last_name"
                  type="text"
                  autocomplete="off"
                />

                <label
                  for="guest-last-name"
                  :class="{
                    active: manualGuest.last_name,
                  }"
                >
                  Last name
                </label>
              </div>

              <div class="input-field col s12 m2">
                <input
                  id="guest-phone-number"
                  v-model="manualGuest.phone_number"
                  type="tel"
                  autocomplete="off"
                />

                <label
                  for="guest-phone-number"
                  :class="{
                    active: manualGuest.phone_number,
                  }"
                >
                  Phone number
                </label>
              </div>

              <div class="input-field col s12 m2">
                <input
                  id="guest-email"
                  v-model="manualGuest.email"
                  type="email"
                  autocomplete="off"
                />

                <label
                  for="guest-email"
                  :class="{
                    active: manualGuest.email,
                  }"
                >
                  Email
                </label>
              </div>

              <div class="input-field col s12 m2">
                <input
                  id="guest-date-of-birth"
                  v-model="manualGuest.date_of_birth"
                  type="date"
                />

                <label for="guest-date-of-birth" class="active">
                  Date of birth
                </label>
              </div>

              <div class="col s12 m1">
                <label for="guest-gender">Gender</label>

                <select
                  id="guest-gender"
                  v-model="manualGuest.gender"
                  class="browser-default"
                >
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div class="col s12 m1 core-mailer-add-guest-button">
                <button
                  type="button"
                  class="btn blue-grey darken-2"
                  :disabled="busy"
                  @click="addManualGuest"
                >
                  Add
                </button>
              </div>
            </div>

            <label>
              <input v-model="manualGuest.subscribed" type="checkbox" />

              <span> May receive marketing email </span>
            </label>
          </div>
        </div>

        <div v-if="csvPreview" class="card core-mailer-csv-preview">
          <div class="card-content">
            <span class="card-title"> CSV import preview </span>

            <p>
              CSV columns: First Name, Last Name, Phone Number, Email, Date of Birth
              and Gender.
            </p>

            <p>
              {{ csvPreview.guests.length }}
              valid unique email addresses,
              {{ csvPreview.duplicates }}
              duplicates and
              {{ csvPreview.invalid.length }}
              invalid rows.
            </p>

            <p>
              Existing guests with the same email address will be updated rather than
              duplicated.
            </p>

            <label class="core-mailer-consent-confirm">
              <input v-model="csvConsentConfirmed" type="checkbox" />

              <span>
                I confirm the imported guests have permission / a lawful basis to receive
                marketing email from this venue.
              </span>
            </label>

            <div class="core-mailer-inline-actions">
              <button
                type="button"
                class="btn blue-grey darken-2"
                :disabled="busy || !csvConsentConfirmed || csvPreview.guests.length === 0"
                @click="confirmCsvImport"
              >
                Import
                {{ csvPreview.guests.length }}
              </button>

              <button
                type="button"
                class="btn-flat"
                :disabled="busy"
                @click="cancelCsvImport"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div class="core-mailer-table-wrap">
          <table class="highlight responsive-table core-mailer-table">
            <thead>
              <tr>
                <th>
                  <label>
                    <input
                      type="checkbox"
                      :checked="allVisibleSelected"
                      @change="toggleVisibleSelection"
                    />

                    <span></span>
                  </label>
                </th>

                <th>Name</th>
                <th>Phone No.</th>
                <th>Email</th>
                <th>Date of Birth</th>
                <th>Gender</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="guest in filteredGuests" :key="guest.id">
                <td>
                  <label>
                    <input
                      type="checkbox"
                      :checked="selectedGuestIds.has(guest.id)"
                      @change="toggleGuestSelection(guest.id)"
                    />

                    <span></span>
                  </label>
                </td>

                <td>
                  {{ guestName(guest) || "—" }}
                </td>

                <td>
                  {{ guest.phone_number || "—" }}
                </td>

                <td>
                  {{ guest.email }}
                </td>

                <td>
                  {{ formatDateOfBirth(guest.date_of_birth) }}
                </td>

                <td>
                  {{ genderLabel(guestGender(guest)) }}
                </td>

                <td class="right-align">
                  <button
                    type="button"
                    class="btn-flat red-text text-darken-2"
                    :disabled="busy"
                    @click="removeGuest(guest)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <tr v-if="filteredGuests.length === 0">
                <td colspan="7" class="center-align grey-text">No guests found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- TEMPLATES -->
      <section v-else-if="activeTab === 'templates'" class="core-mailer-panel">
        <div class="core-mailer-template-layout">
          <aside class="core-mailer-template-list">
            <div class="core-mailer-template-list-heading">
              <strong> Saved templates </strong>

              <button
                type="button"
                class="btn-small blue-grey darken-2"
                @click="newTemplate"
              >
                New
              </button>
            </div>

            <button
              v-for="template in templates"
              :key="template.id"
              type="button"
              class="core-mailer-template-list-item"
              :class="{
                'core-mailer-template-list-item--active':
                  workingTemplate.id === template.id,
              }"
              @click="openTemplate(template)"
            >
              <strong>
                {{ template.name || "Untitled" }}
              </strong>

              <small>
                {{ template.subject || "No subject" }}
              </small>
            </button>

            <p v-if="templates.length === 0" class="grey-text">
              No templates saved yet.
            </p>
          </aside>

          <div class="core-mailer-template-editor">
            <div class="card">
              <div class="card-content">
                <div class="row">
                  <div class="input-field col s12 m4">
                    <input
                      id="mailer-template-name"
                      v-model="workingTemplate.name"
                      type="text"
                    />

                    <label
                      for="mailer-template-name"
                      :class="{
                        active: workingTemplate.name,
                      }"
                    >
                      Template name
                    </label>
                  </div>

                  <div class="input-field col s12 m4">
                    <input
                      id="mailer-template-subject"
                      v-model="workingTemplate.subject"
                      type="text"
                    />

                    <label
                      for="mailer-template-subject"
                      :class="{
                        active: workingTemplate.subject,
                      }"
                    >
                      Default subject
                    </label>
                  </div>

                  <div class="input-field col s12 m4">
                    <input
                      id="mailer-template-preheader"
                      v-model="workingTemplate.preheader"
                      type="text"
                    />

                    <label
                      for="mailer-template-preheader"
                      :class="{
                        active: workingTemplate.preheader,
                      }"
                    >
                      Preheader
                    </label>
                  </div>
                </div>

                <div class="core-mailer-block-toolbar">
                  <button type="button" class="btn-flat" @click="addBlock('text')">
                    + Text
                  </button>

                  <button type="button" class="btn-flat" @click="addBlock('image')">
                    + Image
                  </button>

                  <button type="button" class="btn-flat" @click="addBlock('button')">
                    + Button
                  </button>

                  <button type="button" class="btn-flat" @click="addBlock('divider')">
                    + Divider
                  </button>

                  <button type="button" class="btn-flat" @click="addBlock('spacer')">
                    + Spacer
                  </button>
                </div>

                <p class="grey-text text-darken-1 core-mailer-merge-help">
                  Merge fields:
                  <code v-pre>{{ first_name }}</code
                  >, <code v-pre>{{ last_name }}</code
                  >, <code v-pre>{{ email }}</code
                  >, <code v-pre>{{ venue_name }}</code
                  >. An unsubscribe link is added automatically when sent.
                </p>
              </div>
            </div>

            <div class="core-mailer-canvas">
              <article
                v-for="(block, index) in workingTemplate.blocks"
                :key="block.id"
                class="card core-mailer-block"
                draggable="true"
                @dragstart="startBlockDrag(index)"
                @dragover.prevent
                @drop="dropBlockAt(index)"
              >
                <div class="card-content">
                  <div class="core-mailer-block-heading">
                    <div class="core-mailer-block-drag">
                      <span class="material-icons" aria-hidden="true">
                        drag_indicator
                      </span>

                      <strong>
                        {{ blockLabel(block.type) }}
                      </strong>
                    </div>

                    <div class="core-mailer-block-actions">
                      <button
                        type="button"
                        class="btn-flat"
                        :disabled="index === 0"
                        @click="moveBlock(index, -1)"
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        class="btn-flat"
                        :disabled="index === workingTemplate.blocks.length - 1"
                        @click="moveBlock(index, 1)"
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        class="btn-flat red-text"
                        @click="removeBlock(index)"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <template v-if="block.type === 'text'">
                    <div class="input-field">
                      <textarea
                        :id="`mailer-text-${block.id}`"
                        v-model="block.text"
                        class="materialize-textarea"
                      ></textarea>

                      <label
                        :for="`mailer-text-${block.id}`"
                        :class="{
                          active: block.text,
                        }"
                      >
                        Text
                      </label>
                    </div>

                    <div class="core-mailer-format-row">
                      <label>
                        Font size

                        <input
                          v-model.number="block.fontSize"
                          type="number"
                          min="10"
                          max="42"
                          step="1"
                        />
                      </label>

                      <label>
                        Alignment

                        <select v-model="block.align" class="browser-default">
                          <option value="left">Left</option>
                          <option value="center">Centre</option>
                          <option value="right">Right</option>
                        </select>
                      </label>

                      <label>
                        Text colour

                        <input v-model="block.color" type="color" />
                      </label>

                      <label>
                        <input v-model="block.bold" type="checkbox" />

                        <span> Bold </span>
                      </label>
                    </div>
                  </template>

                  <template v-else-if="block.type === 'image'">
                    <div
                      class="core-mailer-image-placeholder"
                      :style="imagePreviewStyle(block)"
                    >
                      <img
                        v-if="block.imageUrl"
                        :src="block.imageUrl"
                        :alt="block.alt || 'Email image'"
                        :style="imageWidthStyle(block)"
                      />

                      <div v-else>
                        <span class="material-icons" aria-hidden="true"> image </span>

                        <strong> Image placeholder </strong>

                        <small>
                          Upload an image now or save the placeholder for proof.
                        </small>
                      </div>
                    </div>

                    <div class="core-mailer-format-row">
                      <label>
                        <span
                          class="core-mailer-file-button btn-small blue-grey darken-2"
                        >
                          {{ block.imageUrl ? "Replace image" : "Upload image" }}
                        </span>

                        <input
                          class="core-mailer-hidden-input"
                          type="file"
                          accept="image/*"
                          @change="uploadImageForBlock(block, $event)"
                        />
                      </label>

                      <label>
                        Width

                        <input
                          v-model.number="block.widthPercent"
                          type="range"
                          min="20"
                          max="100"
                          step="5"
                        />

                        <span> {{ block.widthPercent }}% </span>
                      </label>

                      <label>
                        Alignment

                        <select v-model="block.align" class="browser-default">
                          <option value="left">Left</option>
                          <option value="center">Centre</option>
                          <option value="right">Right</option>
                        </select>
                      </label>

                      <label class="core-mailer-grow">
                        Alt text

                        <input v-model="block.alt" type="text" />
                      </label>

                      <button
                        v-if="block.imageUrl"
                        type="button"
                        class="btn-flat red-text"
                        @click="clearBlockImage(block)"
                      >
                        Clear
                      </button>
                    </div>
                  </template>

                  <template v-else-if="block.type === 'button'">
                    <div class="row">
                      <div class="input-field col s12 m5">
                        <input
                          :id="`button-label-${block.id}`"
                          v-model="block.label"
                          type="text"
                        />

                        <label
                          :for="`button-label-${block.id}`"
                          :class="{
                            active: block.label,
                          }"
                        >
                          Button label
                        </label>
                      </div>

                      <div class="input-field col s12 m7">
                        <input
                          :id="`button-url-${block.id}`"
                          v-model="block.url"
                          type="url"
                        />

                        <label
                          :for="`button-url-${block.id}`"
                          :class="{
                            active: block.url,
                          }"
                        >
                          Button URL
                        </label>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="block.type === 'spacer'">
                    <label>
                      Spacer height

                      <input
                        v-model.number="block.height"
                        type="range"
                        min="8"
                        max="80"
                        step="4"
                      />

                      <span> {{ block.height }} px </span>
                    </label>
                  </template>

                  <template v-else-if="block.type === 'divider'">
                    <hr />
                  </template>
                </div>
              </article>

              <div
                v-if="workingTemplate.blocks.length === 0"
                class="card-panel grey lighten-4 grey-text text-darken-2 center-align"
              >
                Add a block above to start building the email.
              </div>
            </div>

            <div class="core-mailer-template-actions">
              <button
                type="button"
                class="btn blue-grey darken-2"
                :disabled="busy"
                @click="saveCurrentTemplate"
              >
                Save template
              </button>

              <button
                type="button"
                class="btn-flat"
                :disabled="busy"
                @click="downloadCurrentProof"
              >
                Download PDF proof
              </button>

              <button
                v-if="workingTemplate.id"
                type="button"
                class="btn-flat red-text text-darken-2"
                :disabled="busy"
                @click="removeCurrentTemplate"
              >
                Delete template
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- SEND -->
      <section v-else-if="activeTab === 'send'" class="core-mailer-panel">
        <div class="card">
          <div class="card-content">
            <span class="card-title"> Send campaign </span>

            <div class="row">
              <div class="col s12 m6">
                <label for="mailer-send-template"> Template </label>

                <select
                  id="mailer-send-template"
                  v-model="sendForm.templateId"
                  class="browser-default"
                  @change="applySendTemplateDefaults"
                >
                  <option value="">Choose a template</option>

                  <option
                    v-for="template in templates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.name || "Untitled" }}
                  </option>
                </select>
              </div>

              <div class="input-field col s12 m6">
                <input
                  id="mailer-send-subject"
                  v-model="sendForm.subject"
                  type="text"
                />

                <label
                  for="mailer-send-subject"
                  :class="{
                    active: sendForm.subject,
                  }"
                >
                  Subject
                </label>
              </div>
            </div>

            <div class="core-mailer-recipient-options">
              <label>
                <input
                  v-model="sendForm.recipientMode"
                  type="radio"
                  value="all"
                />

                <span>
                  All subscribed guests ({{ subscribedGuests.length }})
                </span>
              </label>

              <label>
                <input
                  v-model="sendForm.recipientMode"
                  type="radio"
                  value="selected"
                />

                <span>
                  Selected subscribed guests ({{ selectedSubscribedCount }})
                </span>
              </label>

              <label>
                <input
                  v-model="sendForm.recipientMode"
                  type="radio"
                  value="filtered"
                />

                <span>
                  Filtered audience ({{ filteredAudienceGuests.length }})
                </span>
              </label>
            </div>

            <div v-if="sendForm.recipientMode === 'filtered'" class="card grey lighten-5">
              <div class="card-content">
                <span class="card-title">Audience filters</span>

                <div class="row">
                  <div class="col s12 m4">
                    <label for="mailer-filter-gender">Gender</label>

                    <select
                      id="mailer-filter-gender"
                      v-model="sendForm.gender"
                      class="browser-default"
                    >
                      <option value="all">All genders</option>
                      <option value="male">Men</option>
                      <option value="female">Women</option>
                      <option value="unknown">Gender not recorded</option>
                    </select>
                  </div>

                  <div class="input-field col s6 m2">
                    <input
                      id="mailer-filter-min-age"
                      v-model="sendForm.minAge"
                      type="number"
                      min="0"
                      max="120"
                      step="1"
                    />

                    <label
                      for="mailer-filter-min-age"
                      :class="{ active: sendForm.minAge !== '' }"
                    >
                      Minimum age
                    </label>
                  </div>

                  <div class="input-field col s6 m2">
                    <input
                      id="mailer-filter-max-age"
                      v-model="sendForm.maxAge"
                      type="number"
                      min="0"
                      max="120"
                      step="1"
                    />

                    <label
                      for="mailer-filter-max-age"
                      :class="{ active: sendForm.maxAge !== '' }"
                    >
                      Maximum age
                    </label>
                  </div>

                  <div class="col s12 m4 right-align">
                    <button
                      type="button"
                      class="btn blue-grey darken-2"
                      @click="clearAudienceFilters"
                    >
                      Clear filters
                    </button>
                  </div>
                </div>

                <div class="row">
                  <div class="col s12 m6">
                    <label>Birthday from</label>

                    <div class="row">
                      <div class="col s5">
                        <select
                          v-model="sendForm.birthdayFromDay"
                          class="browser-default"
                        >
                          <option value="">Day</option>

                          <option
                            v-for="day in dayOptions"
                            :key="`birthday-from-day-${day}`"
                            :value="String(day).padStart(2, '0')"
                          >
                            {{ day }}
                          </option>
                        </select>
                      </div>

                      <div class="col s7">
                        <select
                          v-model="sendForm.birthdayFromMonth"
                          class="browser-default"
                        >
                          <option value="">Month</option>

                          <option
                            v-for="month in monthOptions"
                            :key="`birthday-from-month-${month.value}`"
                            :value="month.value"
                          >
                            {{ month.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="col s12 m6">
                    <label>Birthday to</label>

                    <div class="row">
                      <div class="col s5">
                        <select
                          v-model="sendForm.birthdayToDay"
                          class="browser-default"
                        >
                          <option value="">Day</option>

                          <option
                            v-for="day in dayOptions"
                            :key="`birthday-to-day-${day}`"
                            :value="String(day).padStart(2, '0')"
                          >
                            {{ day }}
                          </option>
                        </select>
                      </div>

                      <div class="col s7">
                        <select
                          v-model="sendForm.birthdayToMonth"
                          class="browser-default"
                        >
                          <option value="">Month</option>

                          <option
                            v-for="month in monthOptions"
                            :key="`birthday-to-month-${month.value}`"
                            :value="month.value"
                          >
                            {{ month.label }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="grey-text text-darken-1">
                  Filters combine together. The birthday range uses day and month only,
                  so the guest's birth year is ignored. Guests without a date of birth are
                  excluded whenever an age or birthday filter is used.
                </p>

                <strong>
                  {{ filteredAudienceGuests.length }} matching subscribed guests
                </strong>
              </div>
            </div>

            <div class="card-panel amber lighten-5 brown-text text-darken-3">
              Each recipient is sent an individual email. Unsubscribed guests are always
              excluded.
            </div>

            <div class="core-mailer-send-summary">
              <strong>
                {{ sendRecipientCount }}
                recipients
              </strong>
            </div>

            <button
              type="button"
              class="btn blue-grey darken-2"
              :disabled="busy || !canSend"
              @click="sendCampaign"
            >
              {{ busy && busyAction === "send" ? "Sending…" : "Send campaign" }}
            </button>
          </div>
        </div>
      </section>

      <!-- HISTORY -->
      <section v-else-if="activeTab === 'history'" class="core-mailer-panel">
        <div class="core-mailer-table-wrap">
          <table class="highlight responsive-table core-mailer-table">
            <thead>
              <tr>
                <th>Sent</th>
                <th>Campaign</th>
                <th>Subject</th>
                <th>Recipients</th>
                <th>Sent</th>
                <th>Failed</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="campaign in campaigns" :key="campaign.id">
                <td>
                  {{ formatDateTime(campaign.sent_at || campaign.created_at) }}
                </td>

                <td>
                  {{ campaign.template_name || "—" }}
                </td>

                <td>
                  {{ campaign.subject || "—" }}
                </td>

                <td>
                  {{ Number(campaign.recipient_count || 0) }}
                </td>

                <td>
                  {{ Number(campaign.sent_count || 0) }}
                </td>

                <td>
                  {{ Number(campaign.failed_count || 0) }}
                </td>

                <td>
                  {{ campaign.status || "—" }}
                </td>
              </tr>

              <tr v-if="campaigns.length === 0">
                <td colspan="7" class="center-align grey-text">
                  No campaigns sent yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";

import {
  deleteMailerGuest,
  deleteMailerGuests,
  deleteMailerTemplate,
  getMailerBootstrap,
  listAllMailerGuests,
  listMailerCampaigns,
  listMailerTemplates,
  saveMailerTemplate,
  sendMailerCampaign,
  uploadMailerImage,
  upsertMailerGuests,
} from "@/scripts/sqlApiHelper/mailerApi";

import {
  csvToGuestObjects,
  downloadGuestCsvExample,
} from "@/scripts/mailer/mailerCsv";

import { downloadMailerTemplatePdf } from "@/scripts/mailer/mailerPdf";

const props = defineProps({
  venueId: {
    type: String,
    required: true,
  },

  isOperations: {
    type: Boolean,
    default: false,
  },

  isSocialMedia: {
    type: Boolean,
    default: false,
  },
});

const tabs = [
  { key: "guests", label: "Guests" },
  { key: "templates", label: "Templates" },
  { key: "send", label: "Send" },
  { key: "history", label: "History" },
];

const activeTab = ref("guests");
const initialLoading = ref(true);
const busy = ref(false);
const busyAction = ref("");
const errorMessage = ref("");
const successMessage = ref("");
const venueName = ref("");
const guests = ref([]);
const templates = ref([]);
const campaigns = ref([]);
const guestSearch = ref("");
const selectedGuestIds = ref(new Set());
const csvInput = ref(null);
const csvPreview = ref(null);
const csvConsentConfirmed = ref(false);
const draggedBlockIndex = ref(null);

const manualGuest = reactive({
  first_name: "",
  last_name: "",
  phone_number: "",
  email: "",
  date_of_birth: "",
  gender: "",
  subscribed: true,
});

const sendForm = reactive({
  templateId: "",
  subject: "",
  recipientMode: "all",
  gender: "all",
  minAge: "",
  maxAge: "",
  birthdayFromDay: "",
  birthdayFromMonth: "",
  birthdayToDay: "",
  birthdayToMonth: "",
});

const dayOptions = Array.from(
  {
    length: 31,
  },
  (_, index) => index + 1
);

const monthOptions = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function uniqueId(prefix = "block") {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function cleanGender(value) {
  const text = String(value || "").trim().toLowerCase();

  if (["male", "m", "man", "men"].includes(text)) {
    return "male";
  }

  if (["female", "f", "woman", "women"].includes(text)) {
    return "female";
  }

  return "";
}

function genderTag(value) {
  const gender = cleanGender(value);
  return gender ? `gender:${gender}` : "";
}

function guestGender(guest) {
  const direct = cleanGender(guest?.gender);

  if (direct) {
    return direct;
  }

  const tags = Array.isArray(guest?.tags) ? guest.tags : [];

  for (const tag of tags) {
    const text = String(tag || "").trim().toLowerCase();

    if (
      ["gender:male", "gender=male", "male"].includes(text)
    ) {
      return "male";
    }

    if (
      ["gender:female", "gender=female", "female"].includes(text)
    ) {
      return "female";
    }
  }

  return "";
}

function genderLabel(value) {
  if (value === "male") {
    return "Male";
  }

  if (value === "female") {
    return "Female";
  }

  return "—";
}

function validIsoDate(value) {
  const text = String(value || "").trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return "";
  }

  const [year, month, day] = text.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return "";
  }

  return text;
}

function ageFromDob(value, today = new Date()) {
  const dob = validIsoDate(value);

  if (!dob) {
    return null;
  }

  const [year, month, day] = dob.split("-").map(Number);

  let age = today.getFullYear() - year;

  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  if (
    currentMonth < month ||
    (currentMonth === month && currentDay < day)
  ) {
    age -= 1;
  }

  return age >= 0 && age <= 130 ? age : null;
}

function birthdayKey(month, day) {
  const cleanMonth = String(month || "").trim();
  const cleanDay = String(day || "").trim();

  if (
    !/^\d{2}$/.test(cleanMonth) ||
    !/^\d{2}$/.test(cleanDay)
  ) {
    return "";
  }

  const monthNumber = Number(cleanMonth);
  const dayNumber = Number(cleanDay);

  if (
    monthNumber < 1 ||
    monthNumber > 12 ||
    dayNumber < 1 ||
    dayNumber > 31
  ) {
    return "";
  }

  const validationDate = new Date(
    Date.UTC(
      2024,
      monthNumber - 1,
      dayNumber
    )
  );

  if (
    validationDate.getUTCMonth() !== monthNumber - 1 ||
    validationDate.getUTCDate() !== dayNumber
  ) {
    return "";
  }

  return `${cleanMonth}-${cleanDay}`;
}

function birthdayKeyFromDob(value) {
  const dob = validIsoDate(value);

  if (!dob) {
    return "";
  }

  return dob.slice(5);
}

function birthdayIsInRange(
  birthday,
  rangeFrom,
  rangeTo
) {
  if (!birthday) {
    return false;
  }

  if (rangeFrom && rangeTo) {
    if (rangeFrom <= rangeTo) {
      return (
        birthday >= rangeFrom &&
        birthday <= rangeTo
      );
    }

    /*
     * Supports ranges that cross New Year, for example:
     * 15 December -> 15 January.
     */
    return (
      birthday >= rangeFrom ||
      birthday <= rangeTo
    );
  }

  if (rangeFrom) {
    return birthday >= rangeFrom;
  }

  if (rangeTo) {
    return birthday <= rangeTo;
  }

  return true;
}

function audienceGuestMatches(guest) {
  if (guest?.subscribed !== true) {
    return false;
  }

  const requiredGender = sendForm.gender;

  if (
    requiredGender === "male" &&
    guestGender(guest) !== "male"
  ) {
    return false;
  }

  if (
    requiredGender === "female" &&
    guestGender(guest) !== "female"
  ) {
    return false;
  }

  if (
    requiredGender === "unknown" &&
    guestGender(guest)
  ) {
    return false;
  }

  const hasMinAge = String(sendForm.minAge).trim() !== "";
  const hasMaxAge = String(sendForm.maxAge).trim() !== "";

  const birthdayFrom = birthdayKey(
    sendForm.birthdayFromMonth,
    sendForm.birthdayFromDay
  );

  const birthdayTo = birthdayKey(
    sendForm.birthdayToMonth,
    sendForm.birthdayToDay
  );

  if (
    hasMinAge ||
    hasMaxAge ||
    birthdayFrom ||
    birthdayTo
  ) {
    const dob = validIsoDate(guest?.date_of_birth);

    if (!dob) {
      return false;
    }

    if (birthdayFrom || birthdayTo) {
      const guestBirthday = birthdayKeyFromDob(dob);

      if (
        !birthdayIsInRange(
          guestBirthday,
          birthdayFrom,
          birthdayTo
        )
      ) {
        return false;
      }
    }

    if (hasMinAge || hasMaxAge) {
      const age = ageFromDob(dob);

      if (age === null) {
        return false;
      }

      if (
        hasMinAge &&
        age < Number(sendForm.minAge)
      ) {
        return false;
      }

      if (
        hasMaxAge &&
        age > Number(sendForm.maxAge)
      ) {
        return false;
      }
    }
  }

  return true;
}

function emptyTemplate() {
  return {
    id: "",
    name: "",
    subject: "",
    preheader: "",
    blocks: [
      {
        id: uniqueId("text"),
        type: "text",
        text: "Hi {{first_name}},",
        fontSize: 16,
        align: "left",
        color: "#222222",
        bold: false,
      },
      {
        id: uniqueId("image"),
        type: "image",
        imageUrl: "",
        storagePath: "",
        alt: "",
        widthPercent: 100,
        align: "center",
      },
      {
        id: uniqueId("text"),
        type: "text",
        text: "Add your message here.",
        fontSize: 16,
        align: "left",
        color: "#222222",
        bold: false,
      },
    ],
  };
}

const workingTemplate = reactive(emptyTemplate());

const subscribedGuests = computed(() =>
  guests.value.filter((guest) => guest.subscribed === true)
);

const selectedSubscribedCount = computed(
  () =>
    subscribedGuests.value.filter((guest) =>
      selectedGuestIds.value.has(guest.id)
    ).length
);

const selectedGuestCount = computed(() => selectedGuestIds.value.size);

const filteredAudienceGuests = computed(() =>
  subscribedGuests.value.filter(audienceGuestMatches)
);

const filteredGuests = computed(() => {
  const needle = guestSearch.value.trim().toLowerCase();

  if (!needle) {
    return guests.value;
  }

  return guests.value.filter((guest) => {
    const haystack = [
      guest.first_name,
      guest.last_name,
      guest.phone_number,
      guest.email,
      guest.date_of_birth,
      genderLabel(guestGender(guest)),
      ...(Array.isArray(guest.tags) ? guest.tags : []),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(needle);
  });
});

const allVisibleSelected = computed(() => {
  if (!filteredGuests.value.length) {
    return false;
  }

  return filteredGuests.value.every((guest) =>
    selectedGuestIds.value.has(guest.id)
  );
});

const sendRecipientCount = computed(() => {
  if (sendForm.recipientMode === "selected") {
    return selectedSubscribedCount.value;
  }

  if (sendForm.recipientMode === "filtered") {
    return filteredAudienceGuests.value.length;
  }

  return subscribedGuests.value.length;
});

const canSend = computed(
  () =>
    Boolean(sendForm.templateId) &&
    Boolean(sendForm.subject.trim()) &&
    sendRecipientCount.value > 0
);

function clearNotices() {
  errorMessage.value = "";
  successMessage.value = "";
}

function setError(error, fallback = "Something went wrong.") {
  console.error("[MailerStudio]", error);

  errorMessage.value =
    error?.message ||
    error?.details ||
    error?.data?.message ||
    fallback;
}

function clearAudienceFilters() {
  sendForm.gender = "all";
  sendForm.minAge = "";
  sendForm.maxAge = "";
  sendForm.birthdayFromDay = "";
  sendForm.birthdayFromMonth = "";
  sendForm.birthdayToDay = "";
  sendForm.birthdayToMonth = "";
}

function resetWorkingTemplate(template) {
  const next = template ? clone(template) : emptyTemplate();

  Object.keys(workingTemplate).forEach((key) => delete workingTemplate[key]);

  Object.assign(workingTemplate, next);

  if (!Array.isArray(workingTemplate.blocks)) {
    workingTemplate.blocks = [];
  }

  workingTemplate.blocks = workingTemplate.blocks.map((block) => ({
    ...block,
    id: block.id || uniqueId(block.type || "block"),
  }));
}

async function refreshAll() {
  clearNotices();
  initialLoading.value = true;

  try {
    const [
      bootstrap,
      nextGuests,
      nextTemplates,
      nextCampaigns,
    ] = await Promise.all([
      getMailerBootstrap(props.venueId),
      listAllMailerGuests(props.venueId),
      listMailerTemplates(props.venueId),
      listMailerCampaigns(props.venueId),
    ]);

    venueName.value = bootstrap.venueName || props.venueId;
    guests.value = nextGuests;
    templates.value = nextTemplates;
    campaigns.value = nextCampaigns;

    const validIds = new Set(nextGuests.map((guest) => guest.id));

    selectedGuestIds.value = new Set(
      [...selectedGuestIds.value].filter((id) => validIds.has(id))
    );
  } catch (error) {
    setError(error, "Could not load the mailer.");
  } finally {
    initialLoading.value = false;
  }
}

function guestName(guest) {
  return [guest.first_name, guest.last_name].filter(Boolean).join(" ").trim();
}

function formatDateOfBirth(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "—";
  }

  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return text;
  }

  return `${match[3]}/${match[2]}/${match[1]}`;
}

function toggleGuestSelection(guestId) {
  const next = new Set(selectedGuestIds.value);

  if (next.has(guestId)) {
    next.delete(guestId);
  } else {
    next.add(guestId);
  }

  selectedGuestIds.value = next;
}

function toggleVisibleSelection() {
  const next = new Set(selectedGuestIds.value);
  const shouldSelect = !allVisibleSelected.value;

  filteredGuests.value.forEach((guest) => {
    if (shouldSelect) {
      next.add(guest.id);
    } else {
      next.delete(guest.id);
    }
  });

  selectedGuestIds.value = next;
}

async function addManualGuest() {
  clearNotices();

  const email = manualGuest.email.trim().toLowerCase();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errorMessage.value = "Enter a valid guest email address.";
    return;
  }

  busy.value = true;
  busyAction.value = "guest";

  try {
    const tag = genderTag(manualGuest.gender);

    const result = await upsertMailerGuests(
      props.venueId,
      [
        {
          first_name: manualGuest.first_name.trim(),
          last_name: manualGuest.last_name.trim(),
          phone_number: manualGuest.phone_number.trim(),
          email,
          date_of_birth: manualGuest.date_of_birth.trim(),
          tags: tag ? [tag] : [],
          subscribed: manualGuest.subscribed === true,
          source: "manual",
        },
      ]
    );

    successMessage.value =
      result.created > 0 ? "Guest added." : "Guest updated.";

    manualGuest.first_name = "";
    manualGuest.last_name = "";
    manualGuest.phone_number = "";
    manualGuest.email = "";
    manualGuest.date_of_birth = "";
    manualGuest.gender = "";
    manualGuest.subscribed = true;

    guests.value = await listAllMailerGuests(props.venueId);
  } catch (error) {
    setError(error, "Could not save the guest.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

async function handleCsvFile(event) {
  clearNotices();

  const file = event?.target?.files?.[0];

  if (!file) {
    return;
  }

  try {
    const text = await file.text();

    csvPreview.value = csvToGuestObjects(
      text,
      {
        forceSubscribed: true,
      }
    );

    csvConsentConfirmed.value = false;
  } catch (error) {
    setError(error, "Could not read the CSV.");
    csvPreview.value = null;
  } finally {
    if (event?.target) {
      event.target.value = "";
    }
  }
}

async function confirmCsvImport() {
  if (!csvPreview.value || !csvConsentConfirmed.value) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "csv";

  try {
    const result = await upsertMailerGuests(
      props.venueId,
      csvPreview.value.guests
    );

    successMessage.value =
      `CSV import complete: ${result.created} created, ` +
      `${result.updated} updated, ${result.skipped} skipped.`;

    csvPreview.value = null;
    csvConsentConfirmed.value = false;

    guests.value = await listAllMailerGuests(props.venueId);
  } catch (error) {
    setError(error, "CSV import failed.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

function cancelCsvImport() {
  csvPreview.value = null;
  csvConsentConfirmed.value = false;
}

async function removeGuest(guest) {
  if (
    !window.confirm(
      `Delete ${guest.email} from this venue's guest database?`
    )
  ) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "delete-guest";

  try {
    await deleteMailerGuest(props.venueId, guest.id);

    guests.value = guests.value.filter((row) => row.id !== guest.id);

    const next = new Set(selectedGuestIds.value);
    next.delete(guest.id);
    selectedGuestIds.value = next;

    successMessage.value = "Guest deleted.";
  } catch (error) {
    setError(error, "Could not delete the guest.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

async function removeSelectedGuests() {
  const guestIds = [...selectedGuestIds.value];

  if (!guestIds.length) {
    return;
  }

  const count = guestIds.length;

  if (
    !window.confirm(
      `Permanently delete ${count} selected guest${
        count === 1 ? "" : "s"
      } from this venue's guest database?`
    )
  ) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "delete-guests";

  try {
    const result = await deleteMailerGuests(props.venueId, guestIds);

    const deletedIds = new Set(
      Array.isArray(result.deletedIds) ? result.deletedIds : []
    );

    guests.value = guests.value.filter(
      (guest) => !deletedIds.has(guest.id)
    );

    selectedGuestIds.value = new Set(
      [...selectedGuestIds.value].filter(
        (guestId) => !deletedIds.has(guestId)
      )
    );

    const deletedCount = Number(result.deleted || 0);
    const failedCount = Number(result.failed || 0);

    if (failedCount > 0) {
      successMessage.value =
        `${deletedCount} guest${deletedCount === 1 ? "" : "s"} deleted.`;

      errorMessage.value =
        `${failedCount} selected guest${
          failedCount === 1 ? "" : "s"
        } could not be deleted. They remain selected so you can retry.`;
    } else {
      successMessage.value =
        `${deletedCount} guest${deletedCount === 1 ? "" : "s"} deleted.`;
    }
  } catch (error) {
    setError(error, "Could not delete the selected guests.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

function newTemplate() {
  clearNotices();
  resetWorkingTemplate();
  activeTab.value = "templates";
}

function openTemplate(template) {
  clearNotices();
  resetWorkingTemplate(template);
}

function blockLabel(type) {
  return (
    {
      text: "Text",
      image: "Image",
      button: "Button",
      divider: "Divider",
      spacer: "Spacer",
    }[type] || "Block"
  );
}

function addBlock(type) {
  let block;

  if (type === "text") {
    block = {
      id: uniqueId("text"),
      type: "text",
      text: "",
      fontSize: 16,
      align: "left",
      color: "#222222",
      bold: false,
    };
  } else if (type === "image") {
    block = {
      id: uniqueId("image"),
      type: "image",
      imageUrl: "",
      storagePath: "",
      alt: "",
      widthPercent: 100,
      align: "center",
    };
  } else if (type === "button") {
    block = {
      id: uniqueId("button"),
      type: "button",
      label: "Learn more",
      url: "https://",
    };
  } else if (type === "divider") {
    block = {
      id: uniqueId("divider"),
      type: "divider",
    };
  } else {
    block = {
      id: uniqueId("spacer"),
      type: "spacer",
      height: 24,
    };
  }

  workingTemplate.blocks.push(block);
}

function removeBlock(index) {
  workingTemplate.blocks.splice(index, 1);
}

function moveBlock(index, direction) {
  const target = index + direction;

  if (target < 0 || target >= workingTemplate.blocks.length) {
    return;
  }

  const [block] = workingTemplate.blocks.splice(index, 1);
  workingTemplate.blocks.splice(target, 0, block);
}

function startBlockDrag(index) {
  draggedBlockIndex.value = index;
}

function dropBlockAt(index) {
  const from = draggedBlockIndex.value;

  if (from === null || from === index) {
    draggedBlockIndex.value = null;
    return;
  }

  const [block] = workingTemplate.blocks.splice(from, 1);
  workingTemplate.blocks.splice(index, 0, block);
  draggedBlockIndex.value = null;
}

function imagePreviewStyle(block) {
  const justifyContent =
    block.align === "left"
      ? "flex-start"
      : block.align === "right"
      ? "flex-end"
      : "center";

  return {
    justifyContent,
  };
}

function imageWidthStyle(block) {
  return {
    width: `${Math.max(
      20,
      Math.min(100, Number(block.widthPercent || 100))
    )}%`,
  };
}

function clearBlockImage(block) {
  block.imageUrl = "";
  block.storagePath = "";
}

async function uploadImageForBlock(block, event) {
  const file = event?.target?.files?.[0];

  if (!file) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "image";

  try {
    const result = await uploadMailerImage(props.venueId, file);

    block.imageUrl = result.url || "";
    block.storagePath = result.storagePath || "";

    if (!block.imageUrl) {
      throw new Error(
        "The image uploaded, but no public image URL was returned."
      );
    }

    /*
     * IMPORTANT:
     * The live email is rendered from the SAVED template in Firestore,
     * not from the temporary editor state in the browser.
     *
     * Therefore, whenever an image is uploaded to an existing saved
     * template, immediately save the whole template again so imageUrl
     * and storagePath cannot be lost before a campaign is sent.
     */
    if (workingTemplate.id) {
      const saveResult = await saveMailerTemplate(
        props.venueId,
        clone(workingTemplate)
      );

      const saved = saveResult?.template;

      if (saved) {
        const index = templates.value.findIndex(
          (template) => template.id === saved.id
        );

        if (index >= 0) {
          templates.value.splice(index, 1, saved);
        } else {
          templates.value.unshift(saved);
        }

        resetWorkingTemplate(saved);
      }

      successMessage.value = "Image uploaded and template saved.";
    } else {
      successMessage.value =
        "Image uploaded. Save the template before sending it.";
    }
  } catch (error) {
    setError(error, "Image upload failed.");
  } finally {
    if (event?.target) {
      event.target.value = "";
    }

    busy.value = false;
    busyAction.value = "";
  }
}

async function saveCurrentTemplate() {
  clearNotices();

  if (!workingTemplate.name.trim()) {
    errorMessage.value = "Give the template a name before saving.";
    return;
  }

  busy.value = true;
  busyAction.value = "template";

  try {
    const result = await saveMailerTemplate(
      props.venueId,
      clone(workingTemplate)
    );

    const saved = result.template;

    if (saved) {
      const index = templates.value.findIndex(
        (template) => template.id === saved.id
      );

      if (index >= 0) {
        templates.value.splice(index, 1, saved);
      } else {
        templates.value.unshift(saved);
      }

      resetWorkingTemplate(saved);
    }

    successMessage.value = "Template saved.";
  } catch (error) {
    setError(error, "Could not save the template.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

async function removeCurrentTemplate() {
  if (!workingTemplate.id) {
    return;
  }

  if (
    !window.confirm(
      `Delete template "${workingTemplate.name || "Untitled"}"?`
    )
  ) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "delete-template";

  try {
    const deletedId = workingTemplate.id;

    await deleteMailerTemplate(props.venueId, deletedId);

    templates.value = templates.value.filter(
      (template) => template.id !== deletedId
    );

    if (sendForm.templateId === deletedId) {
      sendForm.templateId = "";
      sendForm.subject = "";
    }

    resetWorkingTemplate();
    successMessage.value = "Template deleted.";
  } catch (error) {
    setError(error, "Could not delete the template.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

async function downloadCurrentProof() {
  clearNotices();

  try {
    await downloadMailerTemplatePdf(
      clone(workingTemplate),
      {
        venueId: props.venueId,
        venueName: venueName.value,
      }
    );
  } catch (error) {
    setError(error, "Could not create the PDF proof.");
  }
}

function applySendTemplateDefaults() {
  const template = templates.value.find(
    (row) => row.id === sendForm.templateId
  );

  if (!template) {
    return;
  }

  sendForm.subject = template.subject || "";
}

async function sendCampaign() {
  if (!canSend.value) {
    return;
  }

  const template = templates.value.find(
    (row) => row.id === sendForm.templateId
  );

  if (!template) {
    errorMessage.value = "Choose a saved template.";
    return;
  }

  const count = sendRecipientCount.value;

  if (
    !window.confirm(
      `Send "${sendForm.subject}" to ${count} subscribed guest${
        count === 1 ? "" : "s"
      } from ${venueName.value || props.venueId}?`
    )
  ) {
    return;
  }

  clearNotices();
  busy.value = true;
  busyAction.value = "send";

  try {
    /*
     * If the template currently open in the editor is the one being sent,
     * save it one final time before the Cloud Function reads it.
     * This prevents unsaved image URLs (or other edits) from disappearing
     * from the live email.
     */
    if (
      workingTemplate.id &&
      workingTemplate.id === sendForm.templateId
    ) {
      const saveResult = await saveMailerTemplate(
        props.venueId,
        clone(workingTemplate)
      );

      const saved = saveResult?.template;

      if (saved) {
        const index = templates.value.findIndex(
          (row) => row.id === saved.id
        );

        if (index >= 0) {
          templates.value.splice(index, 1, saved);
        } else {
          templates.value.unshift(saved);
        }

        resetWorkingTemplate(saved);
      }
    }

    let backendRecipientMode = sendForm.recipientMode;
    let recipientIds = [];

    if (sendForm.recipientMode === "selected") {
      recipientIds = [...selectedGuestIds.value];
    } else if (sendForm.recipientMode === "filtered") {
      /*
       * Re-use the existing server-side "selected" send path.
       * The Cloud Function still loads each guest again and excludes anyone
       * who has unsubscribed before sending.
       *
       * This means audience filters do not require a functions/mailer.js
       * replacement or a new Firestore index.
       */
      backendRecipientMode = "selected";
      recipientIds = filteredAudienceGuests.value.map((guest) => guest.id);
    }

    const result = await sendMailerCampaign(
      props.venueId,
      {
        templateId: sendForm.templateId,
        subject: sendForm.subject.trim(),
        recipientMode: backendRecipientMode,
        selectedGuestIds: recipientIds,
      }
    );

    successMessage.value =
      `Campaign complete: ${Number(result.sent || 0)} sent, ` +
      `${Number(result.failed || 0)} failed.`;

    campaigns.value = await listMailerCampaigns(props.venueId);
    activeTab.value = "history";
  } catch (error) {
    setError(error, "Campaign send failed.");
  } finally {
    busy.value = false;
    busyAction.value = "";
  }
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(
    "en-GB",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
}

watch(
  () => props.venueId,
  async (next, previous) => {
    if (!next || next === previous) {
      return;
    }

    selectedGuestIds.value = new Set();
    resetWorkingTemplate();

    sendForm.templateId = "";
    sendForm.subject = "";
    sendForm.recipientMode = "all";
    clearAudienceFilters();

    await refreshAll();
  }
);

onMounted(refreshAll);
</script>
